import { Timestamp } from 'firebase/firestore'
import { isCivilDate } from '@shared/date/civil'
import type { CivilDate } from '@shared/date/civil'
import { isValidCents } from '@shared/money/cents'
import type { Cents } from '@shared/money/cents'

/**
 * **A fronteira, e só ela.** É aqui que `Timestamp` do Firestore vira `string`
 * ISO 8601 e que o que vem do banco passa a valer como `Cents` ou `CivilDate` —
 * em nenhum outro lugar do cliente. O domínio não importa Firebase (ver
 * `Docs/03-Arquitetura/Camadas e dependências.md`), então a tradução mora na
 * borda; a Task 7 da Fase 1 já tinha estabelecido isso dentro de
 * `household-repository.ts`, e este arquivo é a versão compartilhada.
 *
 * ## Leitura tolerante, mas não em cima de dinheiro
 *
 * `household-repository.ts` fixou a regra: campo cosmético que falta ganha
 * default (uma casa sem `name` vira "Casa"), campo que sustenta o significado do
 * documento **lança** (uma casa sem `createdAt` é sinal de que algo que não é o
 * bootstrap escreveu ali).
 *
 * No núcleo financeiro a divisão é a mesma, e a linha cai em outro lugar:
 * **valor, data e classificação nunca ganham default.** Um `amountCents` que
 * virasse `0` porque o campo estava corrompido não aparece em lugar nenhum — a
 * soma continua fechando, com um número a menos dentro dela. É a definição de
 * dinheiro sumindo calado. Por isso as funções `require*` abaixo lançam citando
 * o documento e o campo, e as `optional*` só admitem ausência onde a ausência é
 * um estado real do domínio (`settlementDate` de compra no cartão é `null` para
 * sempre; `path` de categoria recém-criada ainda não existe, porque as regras
 * negam ao cliente escrevê-lo).
 *
 * O rótulo de cada erro é `coleção/id.campo`, montado por quem chama: sem ele,
 * "valor inválido" é verdade e é inútil para achar o documento no banco.
 */

/**
 * `Timestamp` → ISO 8601. Tolera string (já ISO) porque leitura tolerante não
 * custa nada; devolve `null` para o resto, inclusive para o objeto cru
 * `{seconds, nanoseconds}` que um `Timestamp` mal reidratado vira.
 *
 * Mora aqui desde a Task 8, e `household-repository.ts` a importa: eram duas
 * fronteiras de conversão no cliente e a nota promete uma.
 */
export function toIso(value: unknown): string | null {
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (typeof value === 'string') return value
  return null
}

/** Instante obrigatório. `createdAt`/`updatedAt` de qualquer documento nosso. */
export function requireIso(value: unknown, label: string): string {
  const iso = toIso(value)
  if (iso === null) {
    throw new Error(`converters: ${label} não é um instante (Timestamp ou ISO 8601).`)
  }
  return iso
}

/** Texto obrigatório com default: nome, instituição, ícone, cor. Nenhum deles
 * muda um número, e um documento sem eles é feio na tela, não errado. */
export function textOr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback
}

/**
 * Texto obrigatório e não vazio — o `id` do instrumento de uma transação, que é
 * a chave estrangeira para a `Account` ou o `CreditCard`.
 *
 * Sem default: `''` casaria com conta nenhuma e a transação apareceria órfã na
 * tela, sem dizer por quê. Vazio conta como ausente pela mesma razão.
 */
export function requireText(value: unknown, label: string): string {
  if (typeof value !== 'string' || value === '') {
    throw new Error(
      `converters: ${label} precisa ser um texto não vazio, recebi ${JSON.stringify(value)}.`,
    )
  }
  return value
}

/** Texto que o domínio já tipa como anulável — `lastFourDigits`, `note`,
 * `providerItemId`. Ausente e não-texto caem no mesmo `null`. */
export function optionalText(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/**
 * Dinheiro obrigatório. `isValidCents` é `Number.isSafeInteger`, então recusa
 * float, `NaN`, infinito, string e ausência — o conjunto inteiro do que faria
 * uma soma deixar de ser exata.
 */
export function requireCents(value: unknown, label: string): Cents {
  if (!isValidCents(value)) {
    throw new Error(
      `converters: ${label} não é um valor em centavos inteiros, recebi ${JSON.stringify(value)}.`,
    )
  }
  return value
}

/**
 * Dinheiro que o domínio tipa como anulável — `balanceCents`,
 * `creditLimitCents`. Ausente é `null`; **presente e inválido lança**, porque
 * aí não é ausência, é corrupção, e devolver `null` a esconderia.
 */
export function optionalCents(value: unknown, label: string): Cents | null {
  if (value === undefined || value === null) return null
  return requireCents(value, label)
}

/**
 * Data civil obrigatória. `isCivilDate` recusa `'2026-02-30'` — forma certa,
 * dia que não existe —, que é o tipo de data que um provider mal comportado
 * entrega e que jogaria a transação para um mês que ninguém escolheu.
 */
export function requireCivilDate(value: unknown, label: string): CivilDate {
  if (!isCivilDate(value)) {
    throw new Error(
      `converters: ${label} não é uma data civil 'YYYY-MM-DD' existente, recebi ${JSON.stringify(value)}.`,
    )
  }
  return value
}

/** Data civil que o domínio tipa como anulável — `settlementDate`, `postedAt`,
 * `paidAt`. Mesma regra do `optionalCents`: ausente é `null`, inválida lança. */
export function optionalCivilDate(value: unknown, label: string): CivilDate | null {
  if (value === undefined || value === null) return null
  return requireCivilDate(value, label)
}

/**
 * Membro de uma união fechada — `cashImpact`, `status`, `kind`, `brand`.
 *
 * **Lança em vez de cair num default**, e essa é a decisão do arquivo. Um
 * `cashImpact` desconhecido que virasse `'direct'` poria a transação nas duas
 * visões; que virasse `'settlement'` a tiraria das duas. Os dois erram o número
 * do topo do Dashboard, e nenhum dos dois deixa rastro. A união é fechada
 * porque o domínio a fechou; um valor fora dela é documento que alguém escreveu
 * por fora, e é isso que a mensagem diz.
 */
export function requireOneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  label: string,
): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    throw new Error(
      `converters: ${label} precisa ser um de [${allowed.join(', ')}], recebi ${JSON.stringify(value)}.`,
    )
  }
  return value as T
}

/** Inteiro obrigatório — `closingDay`, `dueDay`, `itemCount`, `depth`. */
export function requireInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value)) {
    throw new Error(
      `converters: ${label} precisa ser um inteiro, recebi ${JSON.stringify(value)}.`,
    )
  }
  return value as number
}

/** Booleano com default. `isSystem` ausente é `false`, exatamente o default que
 * `categoryIsSystem()` escolhe em `firestore.rules` — os dois lados têm de
 * responder a mesma coisa sobre o mesmo documento. */
export function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

/**
 * Lista de strings — `path`, `tags`, `patterns`. Ausência vira lista vazia, e
 * lista vazia é estado real: uma categoria recém-criada não tem `path`, porque
 * as regras negam ao cliente escrevê-lo e a Function ainda não passou.
 *
 * Filtra o que não é string em vez de lançar: um elemento estranho no meio de
 * `tags` não vale derrubar a leitura do mês inteiro, e num `path` ele viraria
 * um segmento que `array-contains` nunca casaria de qualquer forma.
 */
export function textList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

/** O sub-objeto de um documento — `source`, `enrichment`, `instrument`. Ausente
 * vira `{}`, e aí quem cobra os campos de dentro é cada `require*`, com o
 * rótulo completo do caminho. */
export function subDocument(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * Membro de uma união fechada **ou `null`** — `paymentMethod`.
 *
 * Tolera o desconhecido, ao contrário de `requireOneOf`, e a diferença é a
 * mesma linha que divide o arquivo inteiro: `paymentMethod` não decide número
 * nenhum, só aparece na tela. Um método que a Pluggy invente e nós não
 * conheçamos vira `null` — *"não sei"* — em vez de derrubar a leitura do mês.
 *
 * `null` e não `'other'`: `'other'` é uma afirmação ("é outro método"), e o que
 * temos é ausência de informação. As duas coisas são diferentes na tela.
 */
export function oneOfOrNull<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) return null
  return value as T
}
